/**
 * Per-user capability grants — the client half of the backend's
 * shared/auth/permission_registry.py.
 *
 * Effective access is the UNION of role-derived access and direct grants, which
 * is why every helper takes the roles that already imply the capability. Keys
 * name capabilities ('budget:read'), not screens, so the same key gates the API
 * call and the element that makes it.
 *
 * These helpers decide what to SHOW. They are not a security boundary — the
 * server-side guard is. Hiding a control the server would refuse is a courtesy
 * to the user, not a control.
 */

import type { User } from '@/types'

/** Roles that may REACH the admin console pages at all — kept in lockstep with
 *  backend ORG_CONSOLE_ROLES and AdminRoute. Reaching a page is not the same as
 *  seeing everything on it; gate individual surfaces on their own role set. */
export const ADMIN_CONSOLE_ROLES = ['admin', 'hr_admin', 'hrbp', 'fno_admin', 'data_team'] as const

/** Role Master (the role database) is admin/hr_admin only — mirrors the server's
 *  ROLE_MASTER_ROLES in role_master.py. hrbp/fno_admin/data_team are deliberately
 *  excluded; fno_admin gets only the read-only /brief listing hr-ops needs. */
export const ROLE_MASTER_ROLES = ['admin', 'hr_admin'] as const

/** Roles that already imply full budget access — backend BUDGET_ROLES. */
export const BUDGET_ROLES = ['admin', 'hr_admin', 'fno_admin'] as const

type MaybeUser = Pick<User, 'roles' | 'permissions'> | null | undefined

export function hasRole(user: MaybeUser, roles: readonly string[]): boolean {
  if (!user?.roles?.length) return false
  return user.roles.some((r) => roles.includes(r))
}

/** True when the user holds this capability outright, or a role that implies it. */
export function hasPermission(
  user: MaybeUser,
  key: string,
  impliedByRoles: readonly string[] = []
): boolean {
  if (impliedByRoles.length && hasRole(user, impliedByRoles)) return true
  return !!user?.permissions?.includes(key)
}

/** True when the user holds ANY of these capabilities, or an implying role. */
export function hasAnyPermission(
  user: MaybeUser,
  keys: readonly string[],
  impliedByRoles: readonly string[] = []
): boolean {
  if (impliedByRoles.length && hasRole(user, impliedByRoles)) return true
  if (!user?.permissions?.length) return false
  return keys.some((k) => user.permissions!.includes(k))
}

/** True when the user has any grant at all — used by route guards to admit
 *  someone who holds no admin role but was granted a single capability. */
export function hasAnyGrant(user: MaybeUser): boolean {
  return !!user?.permissions?.length
}

/** Grants that unlock the Role-Master Budget tab (read implies the tab). */
export const BUDGET_TAB_KEYS = ['budget:read', 'budget:write'] as const

/** Grants that open the Role-Master Profiles tab. Approving is its own key and
 *  is not implied by the HR admin role: it sits with the named approver. */
export const ROLE_PROFILE_KEYS = ['role_profile:edit', 'role_profile:approve'] as const
export const ROLE_PROFILE_APPROVE_KEY = 'role_profile:approve'

/** The grant that opens Admin > Staff order (and the Admin menu link) for someone
 *  without an admin role: the staff apparel order is Finance's. */
export const STAFF_ORDER_KEYS = ['staff_order:manage'] as const
